/**
 * Guess the price direction widget
 *
 * @param {Object} params - Contain params for widget initialization
 * @param {string} params.nodeId - Node id of el to insert widget
 * @param {string} params.lang - Language of static titles
 * @param {array} params.symbols - List of tracked symbols
 * @param {array} params.getParameter - Custom get parameter passed to url
 */

import 'promise-polyfill/src/polyfill'
import 'unfetch/polyfill/index'

import config from './config'
import render from './render'
import utils from './utils'
import api from './api'
import i18n from './i18n'
import WsGate from './wsgate2'
import './main.css'

export default class HotWidget {
    constructor(params) {
        this.id = params.nodeId
        this.getParameter = params.getParameter
        this.strings = i18n[params.lang]
            ? i18n[params.lang]
            : i18n[config.defaultLang]
        this.symbols = params.symbols
        this.limit = params.limit
        this.node = null
    }

    makeRowLink(row) {
        const domain = config.links.buy.domain
        const deepLinkPath = config.links.buy.deepLink.path
        const deepLinkParams = config.links.buy.deepLink.params.replace(
            /PLACEHOLDER/g,
            row.dataset.code
        )

        const link = this.getParameter
            ? `${domain}/${deepLinkPath}?${this.getParameter}&${deepLinkParams}`
            : `${domain}/${deepLinkPath}?${deepLinkParams}`

        return link
    }

    renderWidget() {
        this.node = document.getElementById(this.id)

        if (this.limit) {
            config.limit = this.limit
        }

        api.getInitialNumbers({ url: config.quotes, symbols: this.symbols })
            .then(response => {
                this.data = response
                this.symbols = response.map(i => i.code) // further we'll operate only instruments we have starter data

                // add language path
                let url = config.links.cta.replace(
                    'LANGUAGE/',
                    this.strings.language ? this.strings.language + '/' : ''
                )

                // add custom get params
                if (this.getParameter) {
                    url = url + '?' + this.getParameter
                }

                this.node.innerHTML = render.widget({
                    strings: this.strings,
                    url: url
                })
            })
            .then(() => {
                this.wsgate = new WsGate(config.wsgate)

                // Live update data
                this.wsgate.connect()
                this.wsgate.subscribe(this.symbols)
                this.wsgate.listen(quotes => {
                    utils.updateData({ data2paste: quotes, data: this.data })
                    render.updatePrices({
                        node: this.node,
                        data: this.data,
                        strings: this.strings
                    })
                })

                // Get HOT instruments
                this.wsgate.socket.addEventListener('open', () => {
                    this.connectHOTSocket(this.wsgate.socket)
                })
                this.wsgate.socket.addEventListener('message', event => {
                    if (typeof event.data === 'string') {
                        this.HOTSocketProcessor(JSON.parse(event.data))
                    }
                })

                // each row leads to DEEPLINK onclick
                this.node
                    .querySelector('tbody')
                    .addEventListener('click', event => {
                        const row = event.target.closest('[data-code]')
                        if (!row) return
                        window.location.assign(this.makeRowLink(row))
                    })
            })
            .catch(error => {
                throw new Error(error.message)
            })
    }

    connectHOTSocket(socket) {
        socket.send(
            JSON.stringify({
                name: 'records.subscribe',
                id: '1',
                body: {
                    symbols: this.symbols,
                    types: ['priceRecord', 'highVolatility']
                }
            })
        )
        socket.send(
            JSON.stringify({
                name: 'records.getLastItems',
                id: '2',
                body: {
                    types: ['priceRecord', 'highVolatility'],
                    symbols: this.symbols,
                    from: utils.toISOString(new Date() - 1000 * 60 * 60 * 24)
                }
            })
        )
    }

    HOTSocketProcessor(message) {
        switch (message.name) {
            case 'records.subscribe':
                if (message.body.result !== 'ok') {
                    this.failProcessor({
                        error: message.body.error,
                        context: 'Records subscription failed: '
                    })
                }
                break
            case 'records.getLastItems':
                if (message.body.error === null) {
                    render.printLastItems({
                        items: utils.prepareLastItems(message.body.result),
                        data: this.data,
                        strings: this.strings,
                        node: this.node
                    })
                } else {
                    this.failProcessor({
                        error: message.body.error,
                        context: 'Getting last items failed: '
                    })
                }
                break
            case 'records.highVolatilityEvent':
                if (message.body.error === null) {
                    render.printLastItems({
                        items: [message.body.result.volatilityPeriod],
                        data: this.data,
                        strings: this.strings,
                        node: this.node
                    })
                }
                break
            case 'records.priceRecordsEvent':
                if (message.body.error === null) {
                    render.printLastItems({
                        items: [message.body.result.priceRecord],
                        data: this.data,
                        strings: this.strings,
                        node: this.node
                    })
                }
                break
        }
    }

    failProcessor({ error, context }) {
        this.node.style.display = 'none' // hide the whole widget
        throw new Error(context, error)
    }

    init() {
        if (!this.id) throw new Error('Please, specify the node id!')
        if (!this.symbols) throw new Error('Please, specify the symbols list!')
        else this.renderWidget()
    }
}
